import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { categoriesLabel, supplierCategories, SUPPLIER_CATEGORIES, labelFor } from "@/lib/constants";
import CategoryMultiSelect from "@/components/CategoryMultiSelect";
import VisibilityToggle from "@/components/VisibilityToggle";
import AuthStatusChip from "@/components/AuthStatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useBulkSelect, SelectAllCheckbox, BulkBar } from "@/components/BulkBar";
import { Plus, Trash2, ChevronRight, Search, PieChart, Pencil, ArrowLeftRight } from "lucide-react";
import { ExportMenu } from "@/components/ExportMenu";
import CrmQuickAdd, { ContactCardFields, EMPTY_CONTACT, contactPayload } from "@/components/CrmQuickAdd";
import SortSelect, { sortRecords } from "@/components/SortSelect";
import IndustrySelect from "@/components/IndustrySelect";
import ContributionPanel from "@/components/ContributionPanel";
import TrendPanel from "@/components/TrendPanel";
import { PaginationBar, paginate } from "@/components/Pagination";
import { useScrollRestore } from "@/hooks/useScrollRestore";
import { toast } from "sonner";

const EMPTY = { name: "", industry: "", categories: [], partner_type: "supplier", supplier_type: "international", visibility: "team", ...EMPTY_CONTACT };

export default function Suppliers() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [tab, setTab] = useState("supplier");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sort, setSort] = useState("az");
  const [spend, setSpend] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOwner = (user?.email || "").toLowerCase() === "ronick@somcoregroup.com";
  const sel = useBulkSelect();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCats, setBulkCats] = useState([]);
  const [bulkVisibility, setBulkVisibility] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);

  const load = () => api.get("/suppliers").then((r) => { setSuppliers(r.data); setLoaded(true); });
  useEffect(() => {
    load();
    api.get("/suppliers/analytics").then((r) => setSpend(r.data)).catch(() => {});
  }, []);
  useScrollRestore("suppliers", loaded);

  const create = async () => {
    try {
      const { name, industry, categories, supplier_type, visibility } = form;
      const { data } = await api.post("/suppliers", { name, industry, categories, supplier_type, visibility, partner_type: tab, ...contactPayload(form) });
      setOpen(false); toast.success(tab === "oem" ? "OEM partner created" : "Supplier created");
      navigate(`/suppliers/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (e, id) => {
    if (!isOwner) { e.stopPropagation(); toast.error("Only the account owner can delete suppliers."); return; }
    e.stopPropagation();
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/suppliers/${id}`); load(); toast.success("Deleted");
  };

  const bulkDelete = async () => {
    const ids = [...sel.selected];
    if (!window.confirm(`Delete ${ids.length} record${ids.length !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/suppliers/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} record${data.deleted !== 1 ? "s" : ""}`);
      sel.clear(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };

  const bulkApply = async () => {
    const ids = [...sel.selected];
    const body = { ids };
    if (bulkCats.length) body.categories = bulkCats;
    if (bulkVisibility) body.visibility = bulkVisibility;
    if (!body.categories && !body.visibility) { toast.error("Choose categories or visibility to apply."); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/suppliers/bulk-update", body);
      toast.success(`Updated ${data.updated} record${data.updated !== 1 ? "s" : ""}`);
      setBulkOpen(false); setBulkCats([]); setBulkVisibility(""); sel.clear(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };

  const bulkConvert = async () => {
    const ids = [...sel.selected];
    const target = isOem ? "supplier" : "oem";
    const label = target === "supplier" ? "Supplier" : "OEM Partner";
    if (!window.confirm(`Convert ${ids.length} record${ids.length !== 1 ? "s" : ""} to ${label}?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/suppliers/bulk-update", { ids, partner_type: target });
      toast.success(`Converted ${data.updated} record${data.updated !== 1 ? "s" : ""} to ${label}`);
      sel.clear(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };

  const isOem = tab === "oem";
  const inTab = suppliers.filter((s) => (s.partner_type || "supplier") === tab);
  const usedCats = Array.from(new Set(inTab.flatMap((s) => supplierCategories(s))));
  const rows = inTab.filter((s) => {
    if (catFilter !== "all" && !supplierCategories(s).includes(catFilter)) return false;
    if (typeFilter !== "all" && (s.supplier_type || "international") !== typeFilter) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    const primary = (s.contacts || []).find((x) => x.primary) || (s.contacts || [])[0] || {};
    return [s.name, primary.name, primary.email, s.contact_person, s.contact_email].some((v) => (v || "").toLowerCase().includes(t));
  });
  const paged = paginate(sortRecords(rows, sort), page);
  useEffect(() => { setPage(1); }, [q, catFilter, typeFilter, tab, sort]);

  return (
    <AppLayout title="Suppliers" actions={
      <div className="flex items-center gap-2">
        <ExportMenu kind="suppliers" selected={sel.selected} />
        {isOem
          ? <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-supplier-button"><Plus className="mr-2 h-4 w-4" />New OEM Partner</Button>
          : <CrmQuickAdd kind="supplier" onManual={() => { setForm(EMPTY); setOpen(true); }} onCreated={load} />}
      </div>
    }>
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setCatFilter("all"); setTypeFilter("all"); sel.clear(); }} className="mb-4">
        <TabsList data-testid="supplier-tabs">
          <TabsTrigger value="supplier" data-testid="tab-suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="oem" data-testid="tab-oem">OEM Partners</TabsTrigger>
        </TabsList>
      </Tabs>

      {spend && spend.suppliers.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ContributionPanel
          title="Spend by Supplier"
          subtitle="Where your awarded spend concentrates across suppliers"
          Icon={PieChart}
          currencies={["USD", "TZS", ...spend.currencies.filter((c) => c !== "USD" && c !== "TZS")]}
          metrics={[{ key: "spend", label: "Spend" }]}
          items={spend.suppliers.map((s) => ({
            key: s.supplier_id, label: s.name, badge: s.is_oem ? "OEM" : null,
            meta: `${s.awarded} awarded · ${s.quoted} quoted · ${s.rfqs} RFQ${s.rfqs !== 1 ? "s" : ""}`,
            metricValues: { spend: s.spend },
          }))}
          onOpen={(id) => navigate(`/suppliers/${id}`)}
          emptyHint="No awarded spend yet — award suppliers on RFQ items to see where spend concentrates."
          testid="spend-by-supplier"
        />
        <TrendPanel
          title="Awarded Spend Trend"
          subtitleFor={(cur) => `Monthly awarded spend by supplier (${cur})`}
          endpoint="/suppliers/trends"
          defaultCurrency={(spend.currencies && spend.currencies[0]) || "USD"}
          testid="supplier-trend"
        />
        </div>
      )}

      {isOem && (
        <p className="mb-3 text-sm text-muted-foreground">
          OEM partners have authorised Somcore to represent them in-country. RFQs for their products are shared with a dedicated email format including customer and end-user detail.
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by name, contact or email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="suppliers-search" />
        </div>
        <div className="inline-flex overflow-hidden rounded-lg border border-border" data-testid="suppliers-origin-filter">
          {[["all", "All"], ["local", "Local"], ["international", "International"]].map(([v, lbl]) => (
            <button key={v} onClick={() => setTypeFilter(v)} className={`px-3 py-1.5 text-xs font-semibold transition-colors ${typeFilter === v ? "bg-[#12294B] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`} data-testid={`origin-filter-${v}`}>{lbl}</button>
          ))}
        </div>
        <SortSelect value={sort} onChange={setSort} testid="suppliers-sort" />
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="h-9 w-full text-sm sm:w-56" data-testid="suppliers-category-filter"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {usedCats.map((v) => <SelectItem key={v} value={v} data-testid={`category-option-${v}`}>{labelFor(v, SUPPLIER_CATEGORIES)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="hidden rounded-lg border border-border bg-white overflow-hidden sm:block">
        <table className="data-table w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
              <th className="w-10 px-5 py-3"><SelectAllCheckbox ids={paged.map((s) => s.id)} selected={sel.selected} onToggleAll={sel.toggleAll} /></th>
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Category</th>
              <th className="px-5 py-3 font-medium">Origin</th>
              {isOem && <th className="px-5 py-3 font-medium">Authorised</th>}
              <th className="px-5 py-3 font-medium">Primary Contact</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((s) => {
              const primary = (s.contacts || []).find((x) => x.primary) || (s.contacts || [])[0];
              return (
                <tr key={s.id} onClick={() => sel.selected.size > 0 ? sel.toggle(s.id) : navigate(`/suppliers/${s.id}`)} className={`cursor-pointer border-t border-border hover:bg-slate-50 ${sel.isSelected(s.id) ? "bg-[#EEF3FA]" : ""}`} data-testid={`supplier-row-${s.id}`}>
                  <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={sel.isSelected(s.id)} onCheckedChange={() => sel.toggle(s.id)} aria-label="Select supplier" data-testid={`bulk-select-supplier-${s.id}`} />
                  </td>
                  <td className="px-5 py-3 font-medium text-slate-900">{s.name}</td>
                  <td className="px-5 py-3">{categoriesLabel(s)}</td>
                  <td className="px-5 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${(s.supplier_type || "international") === "local" ? "bg-[#E4EEE8] text-[#527D63]" : "bg-[#E6ECF5] text-[#1B4383]"}`}>{(s.supplier_type || "international") === "local" ? "Local" : "International"}</span>
                  </td>
                  {isOem && <td className="px-5 py-3"><AuthStatusChip auth={s.auth} /></td>}
                  <td className="px-5 py-3 text-muted-foreground">{primary?.name || s.contact_person || "—"}</td>
                  <td className="px-5 py-3 text-muted-foreground">{primary?.email || s.contact_email || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={(e) => remove(e, s.id)} className="text-slate-500 hover:text-destructive" data-testid={`delete-supplier-${s.id}`}><Trash2 className="h-4 w-4" /></button>
                      <ChevronRight className="h-4 w-4 text-slate-300" />
                    </div>
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={isOem ? 8 : 7} className="px-5 py-10 text-center text-muted-foreground">{isOem ? "No OEM partners yet." : "No suppliers yet."}</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="space-y-2.5 sm:hidden" data-testid="suppliers-cards">
        {paged.map((s) => {
          const primary = (s.contacts || []).find((x) => x.primary) || (s.contacts || [])[0];
          const contact = primary?.name || s.contact_person || "";
          const email = primary?.email || s.contact_email || "";
          const local = (s.supplier_type || "international") === "local";
          return (
            <div key={s.id} role="button" tabIndex={0} onClick={() => sel.selected.size > 0 ? sel.toggle(s.id) : navigate(`/suppliers/${s.id}`)} className={`flex w-full items-center gap-3 rounded-lg border border-border p-3.5 text-left active:bg-slate-50 ${sel.isSelected(s.id) ? "bg-[#EEF3FA]" : "bg-white"}`} data-testid={`supplier-card-${s.id}`}>
              <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                <Checkbox checked={sel.isSelected(s.id)} onCheckedChange={() => sel.toggle(s.id)} aria-label="Select supplier" data-testid={`bulk-select-supplier-card-${s.id}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate font-semibold text-slate-900">{s.name}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${local ? "bg-[#E4EEE8] text-[#527D63]" : "bg-[#E6ECF5] text-[#1B4383]"}`}>{local ? "Local" : "International"}</span>
                  {isOem && <AuthStatusChip auth={s.auth} />}
                </div>
                <div className="mt-1 truncate text-xs text-muted-foreground">{categoriesLabel(s)}</div>
                {(contact || email) && <div className="mt-0.5 truncate text-xs text-muted-foreground">{contact || "No contact"}{email ? ` · ${email}` : ""}</div>}
              </div>
              <button onClick={(e) => remove(e, s.id)} className="shrink-0 p-1 text-slate-400 hover:text-destructive" data-testid={`delete-supplier-card-${s.id}`} aria-label="Delete record"><Trash2 className="h-4 w-4" /></button>
              <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
            </div>
          );
        })}
        {!rows.length && <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{isOem ? "No OEM partners found." : "No suppliers found."}</div>}
      </div>

      <PaginationBar page={page} setPage={setPage} total={rows.length} testid="suppliers-pagination" />

      <BulkBar count={sel.selected.size} onClear={sel.clear} onDelete={bulkDelete} canDelete={isOwner} deleting={bulkBusy}>
        <Button size="sm" variant="outline" onClick={bulkConvert} data-testid="bulk-convert-button" className="w-full sm:w-auto">
          <ArrowLeftRight className="mr-1.5 h-4 w-4" />{isOem ? "Convert to Supplier" : "Convert to OEM Partner"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-edit-button" className="w-full sm:w-auto">
          <Pencil className="mr-1.5 h-4 w-4" />Edit
        </Button>
      </BulkBar>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="supplier-bulk-edit-dialog">
          <DialogHeader><DialogTitle>Edit {sel.selected.size} record{sel.selected.size !== 1 ? "s" : ""}</DialogTitle>
          <DialogDescription>Apply categories and/or visibility to all selected records.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label>Categories <span className="font-normal text-muted-foreground">(replaces existing; leave empty to keep)</span></Label>
              <CategoryMultiSelect value={bulkCats} onChange={setBulkCats} testid="bulk-category-select" />
            </div>
            {isAdmin && (
            <div className="space-y-2">
              <Label>Visibility <span className="font-normal text-muted-foreground">(leave blank to keep)</span></Label>
              <VisibilityToggle value={bulkVisibility} onChange={setBulkVisibility} testid="bulk-visibility" />
            </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={bulkApply} disabled={bulkBusy} data-testid="bulk-apply-button">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle>{isOem ? "New OEM Partner" : "New Supplier"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Categories</Label>
              <CategoryMultiSelect value={form.categories} onChange={(v) => setForm({ ...form, categories: v })} testid="supplier-category-select" />
              <p className="text-xs text-muted-foreground">Pick one or more, or type to add a new category.</p>
            </div>
            <div className="space-y-2">
              <Label>Industry</Label>
              <IndustrySelect value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} testid="supplier-industry-select" />
            </div>
            {isAdmin && (
            <div className="flex flex-col items-start gap-2">
              <Label>Visibility</Label>
              <VisibilityToggle value={form.visibility} onChange={(v) => setForm({ ...form, visibility: v })} testid="supplier-visibility" />
            </div>
            )}
            <div className="flex flex-col items-start gap-2">
              <Label>Supplier Type</Label>
              <div className="inline-flex overflow-hidden rounded-md border border-border">
                {[["local", "Local"], ["international", "International"]].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => setForm({ ...form, supplier_type: v })}
                    className={`px-3 py-1.5 text-xs font-medium ${(form.supplier_type || "international") === v ? "bg-[#1B4383] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`new-supplier-type-${v}`}>{lbl}</button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">International runs Air/Road/Sea landed costing; Local uses margin-only costing.</p>
            </div>
            <ContactCardFields kind="supplier" f={form} set={(k, v) => setForm((p) => ({ ...p, [k]: v }))} />
            <p className="text-xs text-muted-foreground">You can add more contacts and notes on the next screen.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={!form.name} data-testid="supplier-save-button">Create & Open</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
